import type { ClientHttpConnector } from '../../client/types.ts';
import { FetchClientHttpConnector } from '../../client/fetch.ts';
import type {
    HttpMethod
} from '../../../types/web/http';
import type {
    TestClient,
    TestClientBuilder,
    MockServerSpec
} from '../../../types/web/test';
import type { ServerCustomizer } from '../../../types/config';
import { HttpHandlerConnector } from './handler.ts';
import { configure, type RouteConfig } from '../../app/route.ts';
import { compose } from '../../common/compose.ts';
import { AsyncLocalStorage } from 'node:async_hooks';
import type { Middleware, ServerWebExchange, SslInfo } from '../../../types/web/server';
import type { SocketRoute } from '../../server/socket.ts';
import { WebHttpHandlerBuilder } from '../../web/server/adapter.ts';

export function bindToApp(app: ServerCustomizer): MockServerSpec {
    return new AppSpec(app);
}

export function bindToServer(): TestClientBuilder {
    return new DefaultTestClientBuilder();
}

class DefaultTestClient implements TestClient {
    readonly #connector: ClientHttpConnector;
    readonly #baseUrl?: string;
    constructor(connector: ClientHttpConnector,
                baseUrl: string | undefined,
                _builder: TestClientBuilder) {
        this.#connector = connector;
        this.#baseUrl = baseUrl;
    }

    fetch = async (input: string | URL, init?: RequestInit): Promise<Response> => {
        try {
            const method = init?.method as HttpMethod ?? 'GET';
            const uri = new URL(input, this.#baseUrl);
            const response = await this.#connector.connect(method, uri, async (request) => {
                // fetch Request to ClientHttpRequest
                const headers = new Headers(init?.headers);
                for (const [name, value] of headers.entries()) {
                    request.headers.add(name, value);
                }
                let body = init?.body ?? Promise.resolve();
                if (body instanceof ReadableStream) {
                    throw new Error(`Unsupported body type ${typeof init?.body} in fetch request`);
                }
                if (body instanceof FormData) {
                    // Convert FormData to URLSearchParams
                    const formDataEntries = Array.from(body.entries());
                    body = new URLSearchParams();
                    for (const [key, value] of formDataEntries) {
                        body.append(key, value.toString());
                    }
                }
                if (body instanceof URLSearchParams) {
                    if (!request.headers.has('content-type')) {
                        request.headers.set('content-type', 'application/x-www-form-urlencoded');
                    }
                    body = body.toString();
                }
                if (body instanceof Blob) {
                    body = await body.bytes();
                }
                if (typeof body === 'string') {
                    body = Buffer.from(body);
                }
                await request.body(body);


            });
            // ClientHttpResponse to fetch Response
            const headers = new Headers();
            for (const key of response.headers.keys()) {
                const value = response.headers.get(key);
                if (value !== undefined) {
                    if (typeof value === 'string') {
                        headers.set(key, value);
                    } else if (Array.isArray(value)) {
                        for (const v of value) {
                            headers.append(key, v);
                        }
                    }
                }
            }
            const status = response.statusCode?.value;
            const body = await response.blob();
            return new Response(body, {status, headers});
        }
        catch (error) {
            return Response.error();
        }

    }
}

class DefaultTestClientBuilder implements TestClientBuilder {
    readonly #httpHandlerBuilder?: Promise<WebHttpHandlerBuilder>;
    #connector?: ClientHttpConnector;
    #sslInfo?: SslInfo;
    #baseUrl: string = 'http://localhost:8080';

    constructor(httpHandlerBuilder?: Promise<WebHttpHandlerBuilder>, connector?: ClientHttpConnector, sslInfo?: SslInfo) {
        if (!(httpHandlerBuilder === undefined || connector === undefined)) {
            throw new Error('You can only set either httpHandlerBuilder or connector, not both.');
        }
        this.#connector = connector;
        this.#sslInfo = sslInfo;
        this.#httpHandlerBuilder = httpHandlerBuilder;
    }

    baseUrl(baseUrl: string): this {
        this.#baseUrl = baseUrl;
        return this;
    }

    clientConnector(connector: ClientHttpConnector): this {
        this.#connector = connector;
        return this;
    }

    async build(): Promise<TestClient> {
        let connector = this.#connector;
        if (connector === undefined) {
            if (this.#httpHandlerBuilder !== undefined) {
                connector = new HttpHandlerConnector((await this.#httpHandlerBuilder).build(), this.#sslInfo);
            }
        }
        connector ??= new FetchClientHttpConnector();

        return new DefaultTestClient(connector, this.#baseUrl, this);
    }
}

abstract class AbstractMockServerSpec implements MockServerSpec {
    #sslInfo?: SslInfo;
    #middleware: Middleware = [];

    middleware(...middleware: Middleware): this {
        this.#middleware = [...(this.#middleware), ...middleware];
        return this;
    }

    sslInfo(sslInfo?: SslInfo): this {
        this.#sslInfo = sslInfo;
        return this;
    }

    configureClient(): TestClientBuilder {
        const builder = this.initHttpHandlerBuilder(...this.#middleware);

        return new DefaultTestClientBuilder(builder, undefined, this.#sslInfo);
    }

    protected abstract initHttpHandlerBuilder(...middleware: Middleware): Promise<WebHttpHandlerBuilder>;

    async build(): Promise<TestClient> {
        return await this.configureClient().build();
    }
}

class AppSpec extends AbstractMockServerSpec {
    readonly #app: ServerCustomizer;
    constructor(app: ServerCustomizer) {
        super();
        this.#app = app;
    }

    protected async initHttpHandlerBuilder(...middleware: Middleware) {
        const routes: RouteConfig = {
            middleware,
            corsConfig: {
                allowOrigin: ['http://localhost:8086']
            },
            resourcesConfig: {
                locations: []
            },
            cors: [],
            authConfig: {type: "none"},
            authorize: [],
            storage: new AsyncLocalStorage<{ exchange: ServerWebExchange }>(),
            sockets: new Map<string, SocketRoute>()
        };

        await configure(this.#app, {}, routes);
        const handler = { handle: compose(...routes.middleware) };
        return WebHttpHandlerBuilder.webHandler(handler);
    }
}
